import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bus, MapPin } from 'lucide-react';
import { BUS_LIVE_COPY } from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import { Card, Chip, EmptyState, ErrorNote, Spinner } from '@/components/ui';
import { apiData } from '@/lib/api';
import { formatRelative, formatTime } from '@/lib/format';
import { loadNaverMaps } from '@/lib/naver-map';

interface LiveStop {
  id: string;
  name: string;
  order: number;
  lat: number | null;
  lng: number | null;
  arriveAt: string | null;
}

interface LiveTrip {
  id: string;
  direction: 'PICKUP' | 'DROPOFF';
  startedAt: string | null;
  endedAt: string | null;
  lastLat: number | null;
  lastLng: number | null;
  lastLocatedAt: string | null;
  /** 접근을 감지한 정거장 — "몇 정거장 전" 계산의 근거 */
  passedStopIds: string[];
}

interface LiveRoute {
  routeId: string;
  routeName: string;
  vehicle: { name: string; plateNo: string } | null;
  myChildren: { id: string; name: string; stopId: string | null; stopName: string | null }[];
  stops: LiveStop[];
  trips: LiveTrip[];
}

const DIRECTION_LABELS = { PICKUP: '등원', DROPOFF: '하원' } as const;

/** 노선 순서와 접근 감지 기록으로 "우리 아이 정거장까지 몇 정거장 전"을 계산한다 */
function progressFor(route: LiveRoute, trip: LiveTrip) {
  const passed = new Set(trip.passedStopIds ?? []);
  const ordered = [...route.stops].sort((a, b) => a.order - b.order);
  const myStopIds = new Set(route.myChildren.map((c) => c.stopId).filter(Boolean));
  if (myStopIds.size === 0) return null;

  const target = ordered.find((s) => myStopIds.has(s.id) && !passed.has(s.id));
  if (!target) return { kind: 'passed' as const };

  const next = ordered.find((s) => !passed.has(s.id));
  const stopsAway = ordered.filter((s) => !passed.has(s.id) && s.order < target.order).length;
  return { kind: 'countdown' as const, stopsAway, targetName: target.name, nextName: next?.name ?? null };
}

/**
 * 버스 실시간 위치 — 명세서 §5.20 P6 (학부모)
 *
 * 운행 중이면 기사 단말이 보낸 최신 좌표를 10초 주기로 새로 고친다.
 * 네이버 지도 키가 있으면 지도에, 없으면 텍스트 현황으로 보여 준다.
 */
export function BusLivePage() {
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['bus', 'live'],
    queryFn: () => apiData<LiveRoute[]>('/bus/live'),
    // 진행 중 운행이 있을 때만 10초, 없으면 60초로 늦춘다
    refetchInterval: (query) => {
      const running = (query.state.data ?? []).some((route) =>
        route.trips.some((trip) => trip.startedAt && !trip.endedAt),
      );
      return running ? 10_000 : 60_000;
    },
  });

  const routes = data ?? [];

  return (
    <Screen>
      <AppBar title={BUS_LIVE_COPY.title} back />

      <div className="flex flex-col gap-3 p-4">
        {isPending && <Spinner />}
        {isError && <ErrorNote message="불러오지 못했어요." onRetry={() => void refetch()} />}

        {!isPending && routes.length === 0 && (
          <EmptyState title={BUS_LIVE_COPY.emptyTitle} body={BUS_LIVE_COPY.emptyBody} />
        )}

        {routes.map((route) => (
          <RouteCard key={route.routeId} route={route} />
        ))}
      </div>
    </Screen>
  );
}

function RouteCard({ route }: { route: LiveRoute }) {
  const active = route.trips.find((trip) => trip.startedAt && !trip.endedAt) ?? null;
  const progress = active ? progressFor(route, active) : null;

  return (
    <Card>
      <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
        <Bus size={18} className="text-primary-strong" />
        <p className="font-semibold">{route.routeName}</p>
        {route.vehicle && (
          <span className="text-sm text-ink-sub">
            {route.vehicle.name} · {route.vehicle.plateNo}
          </span>
        )}
        {active ? (
          <Chip tone="primary" className="ml-auto">
            {DIRECTION_LABELS[active.direction]} {BUS_LIVE_COPY.running}
          </Chip>
        ) : (
          <Chip tone="neutral" className="ml-auto">
            {route.trips.some((t) => t.endedAt) ? BUS_LIVE_COPY.ended : BUS_LIVE_COPY.notStarted}
          </Chip>
        )}
      </div>

      <p className="text-sm text-ink-sub">
        {route.myChildren
          .map((child) => (child.stopName ? `${child.name} · ${child.stopName}` : child.name))
          .join(' / ')}
      </p>

      {/* "몇 정거장 전" — 운행 중 가장 중요한 정보라 지도 위에 크게 둔다 (§5.20 P6) */}
      {active && progress && (
        <div className="mt-2 rounded-md bg-primary-soft px-3 py-2.5">
          {progress.kind === 'passed' ? (
            <p className="font-semibold text-primary-strong">{BUS_LIVE_COPY.minePassed}</p>
          ) : (
            <>
              {progress.nextName && (
                <p className="text-sm text-primary-strong">
                  {BUS_LIVE_COPY.nextStopPrefix} · {progress.nextName}
                </p>
              )}
              <p className="text-lg font-bold text-primary-strong">
                {progress.stopsAway === 0
                  ? BUS_LIVE_COPY.approachingMine
                  : `${BUS_LIVE_COPY.stopsAwayPrefix}${progress.stopsAway}${BUS_LIVE_COPY.stopsAwaySuffix}`}
              </p>
            </>
          )}
        </div>
      )}

      {active &&
        (active.lastLat != null && active.lastLng != null ? (
          <>
            <LiveMap route={route} trip={active} />
            <p className="mt-1.5 text-sm text-ink-sub">
              <MapPin size={12} className="mr-1 inline" />
              {formatRelative(active.lastLocatedAt)} 위치예요
            </p>
          </>
        ) : (
          <p className="mt-2 rounded-sm bg-primary-soft px-3 py-2 text-sm text-primary-strong">
            {BUS_LIVE_COPY.noSignalYet}
          </p>
        ))}

      {route.stops.length > 0 && (
        <ol className="mt-2 flex flex-col gap-1 border-t border-line pt-2">
          {route.stops.map((stop) => {
            const mine = route.myChildren.some((child) => child.stopId === stop.id);
            const passed = Boolean(active && (active.passedStopIds ?? []).includes(stop.id));
            const isNext =
              Boolean(active) &&
              !passed &&
              route.stops
                .filter((s) => !(active!.passedStopIds ?? []).includes(s.id))
                .every((s) => s.order >= stop.order);
            return (
              <li key={stop.id} className={`flex items-center gap-2 text-sm ${passed ? 'opacity-50' : ''}`}>
                <span className="tnum w-5 text-center text-ink-sub">{stop.order + 1}</span>
                <span className={mine ? 'font-semibold' : ''}>{stop.name}</span>
                {mine && <Chip tone="primary">우리 아이</Chip>}
                {passed && <Chip tone="neutral">{BUS_LIVE_COPY.passedMark}</Chip>}
                {isNext && <Chip tone="accent">{BUS_LIVE_COPY.nextMark}</Chip>}
                {stop.arriveAt && <span className="ml-auto text-ink-sub">{stop.arriveAt}</span>}
              </li>
            );
          })}
        </ol>
      )}

      <p className="mt-2 text-sm text-ink-sub">{BUS_LIVE_COPY.approachNote}</p>
    </Card>
  );
}

/**
 * 네이버 지도 — 키가 없거나 로드에 실패하면 텍스트 폴백만 남긴다.
 * 버스 마커는 좌표가 갱신될 때 이동시키고, 지도는 한 번만 만든다.
 */
function LiveMap({ route, trip }: { route: LiveRoute; trip: LiveTrip }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<{ map: unknown; busMarker: unknown; naver: unknown } | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let disposed = false;

    void loadNaverMaps().then((naver) => {
      if (disposed) return;
      if (!naver?.maps || !containerRef.current) {
        setUnavailable(true);
        return;
      }
      if (mapRef.current) return;

      const center = new naver.maps.LatLng(trip.lastLat, trip.lastLng);
      const map = new naver.maps.Map(containerRef.current, { center, zoom: 14 });

      for (const stop of route.stops) {
        if (stop.lat == null || stop.lng == null) continue;
        new naver.maps.Marker({
          position: new naver.maps.LatLng(stop.lat, stop.lng),
          map,
          title: stop.name,
        });
      }

      const busMarker = new naver.maps.Marker({
        position: center,
        map,
        title: route.routeName,
        icon: {
          content: '<div style="font-size:22px;line-height:1">🚌</div>',
          anchor: new naver.maps.Point(11, 11),
        },
        zIndex: 10,
      });

      mapRef.current = { map, busMarker, naver };
    });

    return () => {
      disposed = true;
    };
    // 지도는 최초 1회만 초기화한다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const current = mapRef.current;
    if (!current || trip.lastLat == null || trip.lastLng == null) return;
    /* eslint-disable @typescript-eslint/no-explicit-any */
    const naver = current.naver as any;
    const position = new naver.maps.LatLng(trip.lastLat, trip.lastLng);
    (current.busMarker as any).setPosition(position);
    (current.map as any).panTo(position);
    /* eslint-enable @typescript-eslint/no-explicit-any */
  }, [trip.lastLat, trip.lastLng]);

  if (unavailable) {
    return (
      <p className="mt-2 rounded-sm bg-primary-soft px-3 py-2 text-sm text-primary-strong">
        {BUS_LIVE_COPY.mapUnavailable} {trip.lastLocatedAt && `(${formatTime(trip.lastLocatedAt)} 기준)`}
      </p>
    );
  }

  return <div ref={containerRef} className="mt-2 h-52 w-full overflow-hidden rounded-md" />;
}
